<template>
    <div class="item-form-wrap">
        <!-- Page header -->
        <div class="page-header">
            <div>
                <div class="breadcrumb-label">Položky a ceny</div>
                <h1>{{ status == 1 ? 'Nová položka' : 'Úprava položky' }}</h1>
                <p class="subtitle">Vyplňte základné údaje a nastavte spôsob výpočtu ceny.</p>
            </div>
            <button class="btn-cancel" @click="$emit('cancel')">Zrušiť</button>
        </div>

        <!-- Card: Základné informácie -->
        <div class="card">
            <div class="fields">
                <div class="form-floating" :style="error ? 'border: 1px solid red;' : ''">
                    <input type="text" class="form-control" id="floatingInput" placeholder="Interný názov položky (povinné)" v-model="item_edit.interny_nazov" />
                    <label for="floatingInput">Interný názov položky (povinné)</label>
                </div>
                <div v-if="error" class="field-error-msg">Interný názov je povinný</div>

                <div class="form-floating">
                    <input type="text" class="form-control" id="floatingInput" placeholder="Verejný názov položky" v-model="item_edit.verejny_nazov" />
                    <label for="floatingInput">Verejný názov položky</label>
                </div>

                <div class="fields-row">
                    <div class="form-floating">
                        <input type="text" class="form-control" id="floatingInput" placeholder="ID / SKU" v-model="item_edit.id_sku" />
                        <label for="floatingInput">ID / SKU</label>
                    </div>
                    <div class="form-floating">
                        <input type="text" class="form-control" id="floatingInput" placeholder="Interný popis položky" v-model="item_edit.interny_popis" />
                        <label for="floatingInput">Interný popis položky</label>
                    </div>
                </div>

                <div class="form-floating">
                    <vue-editor v-model="item_edit.verejny_popis" :editorToolbar="customToolbar"></vue-editor>
                    <label class="floating_label_popis">Verejný popis položky</label>
                </div>
            </div>
        </div>

        <!-- Card: Cena a fakturácia -->
        <div class="card">
            <div class="fields">
                <!-- PriceTypeSelect -->
                <div class="field">
                    <label class="field-label-above">Typ ceny</label>
                    <div class="price-type-select" v-click-outside="closePriceDropdown">
                        <button type="button" class="price-type-btn" @click="priceDropdownOpen = !priceDropdownOpen">
                            <div class="price-type-icon" :class="selectedType.iconClass">
                                <span v-html="selectedType.icon"></span>
                            </div>
                            <div class="price-type-info">
                                <div class="price-type-name">{{ selectedType.label }}</div>
                                <div class="price-type-desc">{{ selectedType.desc }}</div>
                            </div>
                            <svg class="chevron" :class="{ 'chevron--open': priceDropdownOpen }" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                        </button>

                        <div v-if="priceDropdownOpen" class="price-type-dropdown">
                            <div
                                v-for="opt in priceTypeOptions"
                                :key="opt.value"
                                class="price-type-option"
                                :class="{ 'price-type-option--active': item_edit.type_cena === opt.value }"
                                @mousedown="selectPriceType(opt.value)"
                            >
                                <div class="price-type-icon" :class="opt.iconClass">
                                    <span v-html="opt.icon"></span>
                                </div>
                                <div class="price-type-info">
                                    <div class="price-type-name">{{ opt.label }}</div>
                                    <div class="price-type-desc">{{ opt.desc }}</div>
                                </div>
                                <svg v-if="item_edit.type_cena === opt.value" class="check-icon" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#13B98C" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Typ: custom (2) — jednotka + počet + sadzba -->
                <template v-if="item_edit.type_cena === 2">
                    <div class="fields-row fields-row--3col">
                        <div class="form-floating">
                            <input type="text" class="form-control" list="unit-list" placeholder="Jednotka" v-model="item_edit.jednotka" />
                            <datalist id="unit-list">
                                <option v-for="u in unitItems" :key="u" :value="u"></option>
                            </datalist>
                            <label>Jednotka</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Počet" v-model.number="item_edit.pocet" />
                            <label>Počet</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Sadzba" v-model.number="item_edit.sadzba" />
                            <label>Sadzba</label>
                        </div>
                    </div>
                </template>

                <!-- Typ: hourly (0) — sadzba + počet hodín -->
                <template v-if="item_edit.type_cena === 0">
                    <div class="fields-row">
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Hodinová sadzba (€/hod)" v-model.number="item_edit.sadzba" />
                            <label>Hodinová sadzba (€/hod)</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Počet hodín" v-model.number="item_edit.pocet" />
                            <label>Počet hodín</label>
                        </div>
                    </div>
                </template>

                <!-- Typ: range (1) — cena od/do -->
                <template v-if="item_edit.type_cena === 1">
                    <div class="fields-row">
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Cena od (bez DPH)" v-model.number="item_edit.cena_bez_dph" />
                            <label>Cena od (bez DPH)</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Cena do (bez DPH)" v-model.number="item_edit.cena_do" />
                            <label>Cena do (bez DPH)</label>
                        </div>
                    </div>
                </template>

                <!-- Typ: hrange (4) — sadzba + hodiny od/do -->
                <template v-if="item_edit.type_cena === 4">
                    <div class="fields-row fields-row--3col">
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Sadzba (€/hod)" v-model.number="item_edit.sadzba" />
                            <label>Sadzba (€/hod)</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Počet hodín od" v-model.number="item_edit.pocet" />
                            <label>Počet hodín od</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Počet hodín do" v-model.number="item_edit.pocet_do" />
                            <label>Počet hodín do</label>
                        </div>
                    </div>
                </template>

                <!-- Cena bez DPH (onlyprice + custom) -->
                <template v-if="item_edit.type_cena === null || item_edit.type_cena === 2">
                    <div class="form-floating">
                        <input type="number" min="0" class="form-control" placeholder="Cena položky (bez DPH)" v-model.number="item_edit.cena_bez_dph" />
                        <label>Cena položky (bez DPH)</label>
                    </div>
                </template>

                <!-- Výsledná cena bez DPH pre hourly (0) — editovateľná -->
                <template v-if="item_edit.type_cena === 0">
                    <div class="form-floating">
                        <input type="number" min="0" class="form-control" placeholder="Výsledná cena (bez DPH)" v-model.number="item_edit.cena_bez_dph" />
                        <label>Výsledná cena (bez DPH)</label>
                    </div>
                </template>

                <!-- Výsledná cena od – do (bez DPH) pre hrange (4) — editovateľná -->
                <template v-if="item_edit.type_cena === 4">
                    <div class="fields-row">
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Výsledná cena od (bez DPH)" v-model.number="item_edit.cena_bez_dph" />
                            <label>Výsledná cena od (bez DPH)</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" min="0" class="form-control" placeholder="Výsledná cena do (bez DPH)" v-model.number="item_edit.cena_do" />
                            <label>Výsledná cena do (bez DPH)</label>
                        </div>
                    </div>
                </template>

                <!-- DPH — vždy viditeľné pri každom type -->
                <div class="dph-row">
                    <div class="dph-toggle" @click="dph_status = !dph_status">
                        <div class="dph-checkbox" :class="{ 'dph-checkbox--on': dph_status }">
                            <svg v-if="dph_status" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </div>
                        <span class="dph-label">Nastaviť cenu s DPH</span>
                    </div>
                    <div v-if="dph_status" class="dph-rate">
                        <span>Sadzba</span>
                        <input type="text" v-model.number="item_edit.dph" @keypress="dphCheck" />
                        <span>% DPH</span>
                    </div>
                </div>

                <!-- Cena s DPH — len ak je DPH zapnuté -->
                <template v-if="dph_status">
                <!-- Cena s DPH — pre range (1, 4) obe inputy editovateľné -->
                <template v-if="item_edit.type_cena === 1 || item_edit.type_cena === 4">
                    <div class="fields-row">
                        <div class="form-floating">
                            <input type="number" class="form-control form-control--green" placeholder="Cena od (s DPH)" v-model.number="item_edit.cena_s_dph" />
                            <label>Cena od (s DPH)</label>
                        </div>
                        <div class="form-floating">
                            <input type="number" class="form-control form-control--green" placeholder="Cena do (s DPH)" v-model.number="item_edit.cena_s_dph_do" />
                            <label>Cena do (s DPH)</label>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="form-floating">
                        <input type="number" class="form-control form-control--green" placeholder="Cena položky (s DPH)" v-model.number="item_edit.cena_s_dph" />
                        <label>Cena položky (s DPH)</label>
                    </div>
                </template>
                </template>
            </div>
        </div>

        <!-- Save button -->
        <button class="btn-save" @click="save">
            <span v-show="!saved">Uložiť položku</span>
            <div v-show="saved" class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
        </button>
    </div>
</template>

<script>
import { VueEditor } from "vue2-editor";

export default {
    components: { VueEditor },
    props: ["status", "user_id", "item_edit"],
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(); };
                document.addEventListener('mousedown', el._clickOutside);
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._clickOutside);
            }
        }
    },
    data() {
        return {
            error: false,
            dph_status: false,
            saved: false,
            priceDropdownOpen: false,
            unitItems: ["ks", "m2", "hod", "bm", "kpl", "m", "km", "NS", "MD", "MH"],
            unitItemsLoaded: false,
            customToolbar: [
                ["bold", "italic", "underline", "link"],
                [{ list: "ordered" }, { list: "bullet" }],
            ],
            priceTypeOptions: [
                {
                    value: null,
                    label: 'Len cena',
                    desc: 'Jedna pevná suma bez ďalších polí',
                    iconClass: 'card-icon--green',
                    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M15 9.3a3.5 3.5 0 1 0 0 5.4"></path><line x1="6.5" y1="11" x2="12.5" y2="11"></line><line x1="6.5" y1="13.4" x2="11.5" y2="13.4"></line></svg>',
                },
                {
                    value: 0,
                    label: 'Cena za hodinovú sadzbu',
                    desc: 'Sadzba za hodinu × počet hodín',
                    iconClass: 'card-icon--blue',
                    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><polyline points="12 7 12 12 15 14"></polyline></svg>',
                },
                {
                    value: 1,
                    label: 'Cenový rozsah od – do',
                    desc: 'Min a max cena pre flexibilné ponuky',
                    iconClass: 'card-icon--purple',
                    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><polyline points="7 8 3 12 7 16"></polyline><polyline points="17 8 21 12 17 16"></polyline></svg>',
                },
                {
                    value: 2,
                    label: 'Vlastná cena',
                    desc: 'Plná kontrola nad jednotkou a sadzbou',
                    iconClass: 'card-icon--orange',
                    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"></path><circle cx="7" cy="7" r="1.4"></circle></svg>',
                },
                {
                    value: 4,
                    label: 'Hodinový rozsah od – do',
                    desc: 'Odhad času v rozpätí hodín',
                    iconClass: 'card-icon--pink',
                    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="10" y1="2" x2="14" y2="2"></line><circle cx="12" cy="14" r="8"></circle><line x1="12" y1="14" x2="12" y2="10"></line></svg>',
                },
            ],
        };
    },
    computed: {
        selectedType() {
            return this.priceTypeOptions.find(o => o.value === this.item_edit.type_cena) || this.priceTypeOptions[0];
        },
    },
    mounted() {
        this.loadUnits();
        if (this.status == 2) {
            if (this.item_edit.cena_s_dph != null) this.dph_status = true;
            if (!this.item_edit.dph) this.item_edit.dph = 0;
            if (this.item_edit.type_cena === undefined) this.item_edit.type_cena = null;
            if (!this.item_edit.jednotka) this.item_edit.jednotka = 'ks';
            if (this.item_edit.pocet === undefined) this.item_edit.pocet = null;
            if (this.item_edit.pocet_do === undefined) this.item_edit.pocet_do = null;
            if (this.item_edit.sadzba === undefined) this.item_edit.sadzba = null;
            if (this.item_edit.cena_do === undefined) this.item_edit.cena_do = null;
            if (this.item_edit.cena_s_dph_do === undefined) this.item_edit.cena_s_dph_do = null;
        } else {
            this.$set(this.item_edit, 'type_cena', null);
            this.$set(this.item_edit, 'jednotka', 'ks');
            this.$set(this.item_edit, 'pocet', null);
            this.$set(this.item_edit, 'pocet_do', null);
            this.$set(this.item_edit, 'sadzba', null);
            this.$set(this.item_edit, 'cena_do', null);
            this.$set(this.item_edit, 'cena_s_dph_do', null);
            if (!this.item_edit.dph) this.item_edit.dph = 0;
        }
    },
    methods: {
        loadUnits() {
            axios.post("/api/offer/units").then((response) => {
                const fromDb = Array.isArray(response.data)
                    ? response.data.map(u => u.name)
                    : (response.data && response.data.name ? [response.data.name] : []);
                const base = ["ks", "m2", "hod", "bm", "kpl", "m", "km", "NS", "MD", "MH"];
                this.unitItems = Array.from(new Set([...base, ...fromDb]));
            }).catch(() => {});
        },
        closePriceDropdown() {
            this.priceDropdownOpen = false;
        },
        selectPriceType(val) {
            this.item_edit.type_cena = val;
            this.priceDropdownOpen = false;
        },
        formatPrice(val) {
            const n = parseFloat(val) || 0;
            return n.toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
        },
        save() {
            if (!this.item_edit.interny_nazov || this.item_edit.interny_nazov.length <= 0) {
                this.error = true;
                window.scrollTo(0, 0);
                return;
            }
            this.saved = true;
            const data = {
                id:            this.item_edit.id,
                id_sku:        this.item_edit.id_sku,
                user_id:       this.user_id,
                interny_nazov: this.item_edit.interny_nazov,
                verejny_nazov: this.item_edit.verejny_nazov,
                interny_popis: this.item_edit.interny_popis,
                verejny_popis: this.item_edit.verejny_popis,
                cena_bez_dph:  this.item_edit.cena_bez_dph,
                cena_s_dph:    this.dph_status ? this.item_edit.cena_s_dph : null,
                cena_s_dph_do: (this.dph_status && (this.item_edit.type_cena === 1 || this.item_edit.type_cena === 4)) ? this.item_edit.cena_s_dph_do : null,
                dph:           this.dph_status ? this.item_edit.dph : null,
                type_cena:     this.item_edit.type_cena,
                jednotka:      this.item_edit.type_cena === 2 ? this.item_edit.jednotka : null,
                pocet:         this.item_edit.pocet,
                pocet_do:      this.item_edit.pocet_do,
                sadzba:        this.item_edit.sadzba,
                cena_do:       (this.item_edit.type_cena === 1 || this.item_edit.type_cena === 4) ? this.item_edit.cena_do : null,
            };
            axios.post("/api/saveItem", data).then((response) => {
                this.saved = false;
                this.$emit("save", response.data);
            });
        },
        dphCheck(event) {
            const keysAllowed = ["0","1","2","3","4","5","6","7","8","9"];
            if (!keysAllowed.includes(event.key) || String(this.item_edit.dph || '').length > 2) {
                event.preventDefault();
            }
        },
        calc_price() {
            if (!this.dph_status) return;
            const rate = parseFloat(this.item_edit.dph) || 0;
            if (this.item_edit.cena_bez_dph) {
                const base = parseFloat(this.item_edit.cena_bez_dph) || 0;
                this.item_edit.cena_s_dph = parseFloat((base * (1 + rate / 100)).toFixed(2));
            }
            if ((this.item_edit.type_cena === 1 || this.item_edit.type_cena === 4) && this.item_edit.cena_do) {
                const baseDo = parseFloat(this.item_edit.cena_do) || 0;
                this.item_edit.cena_s_dph_do = parseFloat((baseDo * (1 + rate / 100)).toFixed(2));
            }
        },
        calc_cena_bez_dph() {
            const type = this.item_edit.type_cena;
            if ((type === 0 || type === 2 || type === 4) && this.item_edit.pocet && this.item_edit.sadzba) {
                this.item_edit.cena_bez_dph = parseFloat((this.item_edit.pocet * this.item_edit.sadzba).toFixed(2));
            }
            if (type === 4 && this.item_edit.pocet_do && this.item_edit.sadzba) {
                this.item_edit.cena_do = parseFloat((this.item_edit.pocet_do * this.item_edit.sadzba).toFixed(2));
            }
            this.calc_price();
        },
    },
    watch: {
        dph_status() { this.calc_price(); },
        "item_edit.dph"() { this.calc_price(); },
        "item_edit.cena_bez_dph"() { this.calc_price(); },
        "item_edit.pocet"() { this.calc_cena_bez_dph(); },
        "item_edit.pocet_do"() { this.calc_cena_bez_dph(); },
        "item_edit.sadzba"() { this.calc_cena_bez_dph(); },
        "item_edit.cena_do"() { this.calc_price(); },
        "item_edit.interny_nazov"() {
            if (this.error && this.item_edit.interny_nazov && this.item_edit.interny_nazov.length > 0) {
                this.error = false;
            }
        },
    },
};
</script>

<style lang="scss">
.ql-toolbar.ql-snow + .ql-container.ql-snow { padding-top: 8px !important; padding-left: 12px !important; min-height: 90px !important; }
.ql-snow .ql-tooltip a.ql-action::after { content: 'Upraviť' !important; }
.ql-snow .ql-tooltip a.ql-remove::before { content: 'Vymazať' !important; }
.ql-snow .ql-tooltip::before, .ql-snow .ql-tooltip[data-mode=link]::before { content: "URL:" !important; }
.ql-snow .ql-tooltip.ql-editing a.ql-action::after { content: 'Uložiť' !important; }
.ql-snow .ql-tooltip { transform: translate(85px, 10px) !important; }
.ql-container.ql-snow { border: 1px solid #E2E6EC !important; border-top: 0 !important; border-radius: 0 0 11px 11px !important; }
.ql-toolbar.ql-snow { border: 1px solid #E2E6EC !important; border-radius: 11px 11px 0 0 !important; margin-top: 0; background: #FAFBFC; }
.ql-snow .ql-toolbar button, .ql-snow.ql-toolbar button { margin-right: 6px !important; svg { width: 18px !important; } }
</style>

<style lang="scss" scoped>
.item-form-wrap {
    max-width: 720px;
    margin: 0 auto;
    padding: 18px 20px 40px;
    font-family: 'Roboto', system-ui, sans-serif;
}

.page-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    .breadcrumb-label {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .1em;
        color: #9AA3AE;
        text-transform: uppercase;
        margin-bottom: 3px;
    }
    h1 {
        margin: 0 0 2px;
        font-size: 20px;
        font-weight: 800;
        color: #222A38;
    }
    .subtitle {
        margin: 0;
        font-size: 12px;
        color: #7A828F;
    }
}

.btn-cancel {
    border: 1px solid #DCE2EA;
    background: #fff;
    color: #5A6473;
    font-weight: 700;
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 9px;
    cursor: pointer;
    flex: none;
    transition: background .15s;
    &:hover { background: #F4F7FB; }
}

.card {
    background: #fff;
    border: 1px solid #EAEDF2;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(30, 45, 70, .05);
    padding: 18px 20px;
    margin-bottom: 12px;
}


.fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.fields-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    &--3col { grid-template-columns: 1fr 1fr 1fr; }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.field { display: flex; flex-direction: column; gap: 5px; }

.field-label-above {
    display: block;
    font-size: 10px;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: #9AA3AE;
    font-weight: 700;
    margin-bottom: 5px;
}

.field-error-msg {
    font-size: 11px;
    color: #F3385A;
    font-weight: 600;
    margin-top: 3px;
}

.floating_label_popis {
    top: 38px !important;
    left: 0px !important;
}

.form-floating {
    width: 100%;

    > label {
        font-family: Roboto, sans-serif;
        font-style: normal;
        font-weight: bold;
        font-size: 12px;
        letter-spacing: .1em;
        text-transform: uppercase;
        color: #9598a7;
        padding: 0.7rem 0.75rem;
    }

    > .form-control,
    input.form-control {
        height: calc(2.9rem + 2px) !important;
        min-height: calc(2.9rem + 2px) !important;
        padding: 1.05rem 0.75rem 0.25rem !important;
        background: #ffffff;
        border: 1px solid #dde3e8;
        box-sizing: border-box;
        border-radius: 5px;
        font-family: Roboto;
        font-weight: 400;
        font-size: 14px;
        line-height: 1.2;
        color: #212541;
    }

    > .form-control:focus ~ label,
    > .form-control:not(:placeholder-shown) ~ label {
        transform: scale(.8) translateY(-0.45rem) translateX(0.15rem);
    }
}

.form-control--green {
    color: #13B98C !important;
    font-weight: 700 !important;
}

.item-combobox {
    .v-input {
        height: 58px;
        width: 100%;
    }

    .theme--light.v-text-field--filled > .v-input__control > .v-input__slot {
        background: #fff;
    }

    .v-input__slot {
        &:hover { background: #fff !important; }

        border: 1px solid #dde3e8 !important;
        border-radius: 5px !important;

        input {
            font-style: normal;
            font-weight: normal;
            font-size: 14px;
            line-height: 16px;
            color: #212541 !important;
            border: none !important;
            min-height: unset !important;
        }
    }

    .v-text-field > .v-input__control > .v-input__slot {
        &::after { content: none !important; }
        &::before { content: none !important; }
    }

    .v-label.v-label--active.theme--light {
        left: -8px !important;
        top: 12px !important;
        font-family: Roboto !important;
        font-style: normal !important;
        font-weight: bold !important;
        font-size: 14px !important;
        text-transform: uppercase !important;
        line-height: 16px !important;
        color: #212541 !important;
        opacity: 0.65 !important;
        margin-bottom: 0;
    }

    .v-text-field__details { display: none !important; }
}

// PriceTypeSelect
.price-type-select {
    position: relative;
}

.card-icon {
    &--green  { background: #E6FAF4; color: #13B98C; }
    &--orange { background: #FEF1E0; color: #E08A1E; }
    &--blue   { background: #E7F0FD; color: #2F7BE0; }
    &--purple { background: #ECEDFD; color: #5B5FE0; }
    &--pink   { background: #FDE9EE; color: #E1466F; }
}

.price-type-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #E2E6EC;
    border-radius: 9px;
    padding: 7px 10px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: border-color .15s;

    &:hover { border-color: #CFD5DE; }

    .price-type-icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        svg { width: 16px; height: 16px; }
    }
}

.price-type-info {
    flex: 1;
    min-width: 0;
}
.price-type-name {
    font-weight: 700;
    font-size: 13px;
    color: #2B3240;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.price-type-desc {
    font-size: 11px;
    color: #8A93A6;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.chevron {
    flex: none;
    color: #A6AEBA;
    transition: transform .2s;
    &--open { transform: rotate(180deg); }
}

.price-type-dropdown {
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    right: 0;
    z-index: 50;
    background: #fff;
    border: 1px solid #EAEDF2;
    border-radius: 11px;
    box-shadow: 0 12px 30px rgba(22, 32, 52, .14);
    padding: 5px;
}

.price-type-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 9px;
    border-radius: 8px;
    cursor: pointer;
    transition: background .12s;

    &:hover { background: #F4F7FB; }
    &--active { background: #F4F7FB; }

    .price-type-icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        svg { width: 16px; height: 16px; }
    }
}

.check-icon {
    flex: none;
    margin-left: auto;
}

// Computed price row
.computed-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 13px;
    background: #F6F9FC;
    border-radius: 9px;
}
.computed-label {
    font-size: 11px;
    font-weight: 700;
    color: #7A828F;
    text-transform: uppercase;
    letter-spacing: .05em;
}
.computed-value {
    font-size: 14px;
    font-weight: 800;
    color: #2B3240;
}
.computed-row--green {
    background: #E9FBF4;
}
.computed-value--green {
    color: #13B98C;
}

// DPH row
.dph-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 13px;
    background: #F6F9FC;
    border-radius: 9px;
}

.dph-toggle {
    display: flex;
    align-items: center;
    gap: 9px;
    cursor: pointer;
    user-select: none;
}

.dph-checkbox {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 2px solid #CFD5DE;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    transition: background .15s, border-color .15s;

    svg { width: 11px; height: 11px; }

    &--on {
        background: #1ECFA0;
        border-color: #1ECFA0;
    }
}

.dph-label {
    font-size: 13px;
    font-weight: 700;
    color: #3C4453;
}

.dph-rate {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #7A828F;
    font-weight: 600;

    input {
        width: 44px;
        text-align: center;
        border: 1px solid #E2E6EC;
        border-radius: 7px;
        padding: 5px;
        font-size: 13px;
        font-weight: 700;
        color: #2B3240;
        background: #fff;
        outline: none;
        font-family: inherit;
        transition: border-color .15s;

        &:focus { border-color: #1ECFA0; }
    }
}

// Save button
.btn-save {
    width: 100%;
    margin-top: 4px;
    border: none;
    background: linear-gradient(135deg, #27D6A4, #14BF93);
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    padding: 13px;
    border-radius: 11px;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(20, 191, 147, .28);
    font-family: inherit;
    transition: filter .15s;
    min-height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover { filter: brightness(1.04); }
}

// Ellipsis loader
.lds-ellipsis {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 24px;

    div {
        position: absolute;
        top: 5px;
        width: 13px;
        height: 13px;
        border-radius: 50%;
        background: #fff;
        animation-timing-function: cubic-bezier(0, 1, 1, 0);

        &:nth-child(1) { left: 8px; animation: lds-e1 0.6s infinite; }
        &:nth-child(2) { left: 8px; animation: lds-e2 0.6s infinite; }
        &:nth-child(3) { left: 32px; animation: lds-e2 0.6s infinite; }
        &:nth-child(4) { left: 56px; animation: lds-e3 0.6s infinite; }
    }
}
@keyframes lds-e1 { 0% { transform: scale(0); } 100% { transform: scale(1); } }
@keyframes lds-e3 { 0% { transform: scale(1); } 100% { transform: scale(0); } }
@keyframes lds-e2 { 0% { transform: translate(0, 0); } 100% { transform: translate(24px, 0); } }
</style>
